import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { IcoDownload, IcoCheck, IcoTrash } from '../dashboard/DashIcons';
import ConfirmDeleteModal from '../modals/ConfirmDeleteModal';
import '../../styles/admin-dashboard.css';

const MANAGER_ROLES = ['manager', 'adminassistant', 'admin', 'superadmin'];

const EMPTY_FORM = {
  managerId:  '',
  date:       new Date().toISOString().split('T')[0],
  reg:        '',
  ot1:        '',
  ot2:        '',
  hol:        '',
  sic:        '',
  pto:        '',
  meeting:    '',
  training:   '',
  lunch:      '',
  mileage:    '',
  otherPay:   '',
  notes:      '',
};

function num(v) { return parseFloat(v) || 0; }

export default function ManagerTimeEntryPage() {
  const { user, isSuperAdmin } = useAuth();
  const token = user?.token;

  const [managers,      setManagers]      = useState([]);
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [submitting,    setSubmitting]    = useState(false);
  const [submitMsg,     setSubmitMsg]     = useState('');
  const [submitError,   setSubmitError]   = useState('');
  const [entries,       setEntries]       = useState([]);
  const [loadingEntries,setLoadingEntries]= useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterMgr,     setFilterMgr]    = useState('');

  // Date range — current month by default
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const [rangeStart, setRangeStart] = useState(firstOfMonth);
  const [rangeEnd,   setRangeEnd]   = useState(today.toISOString().split('T')[0]);

  const fetchManagers = useCallback(async () => {
    try {
      const res = await fetch('/api/employees', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      setManagers((data.employees || []).filter(e => MANAGER_ROLES.includes(e.role)));
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const params = new URLSearchParams({ startDate: rangeStart, endDate: rangeEnd });
      const res = await fetch(`/api/payroll/summary?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      const mgrEntries = (data.employees || [])
        .filter(e => MANAGER_ROLES.includes(e.role))
        .flatMap(e => (e.entries || []).map(en => ({
          ...en,
          employeeName: e.employeeName,
          employeeId:   e.employeeId,
        })))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setEntries(mgrEntries);
    } catch (err) { console.error(err); }
    finally { setLoadingEntries(false); }
  }, [token, rangeStart, rangeEnd]);

  useEffect(() => { fetchManagers(); }, [fetchManagers]);
  useEffect(() => { fetchEntries(); },  [fetchEntries]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitMsg('');
    if (!form.managerId) { setSubmitError('Please select a manager.'); return; }
    if (!form.date)      { setSubmitError('Please select a date.'); return; }
    const mgr = managers.find(m => m._id === form.managerId);
    if (!mgr) { setSubmitError('Manager not found.'); return; }

    const hoursWorked = num(form.reg) + num(form.ot1) + num(form.ot2) +
                        num(form.hol) + num(form.sic) + num(form.pto) +
                        num(form.meeting) + num(form.training);

    if (hoursWorked <= 0) { setSubmitError('At least one hour type must be greater than 0.'); return; }

    setSubmitting(true);
    try {
      const res = await fetch('/api/timeEntries', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({
          firstName:   mgr.firstName,
          lastName:    mgr.lastName,
          date:        form.date,
          hoursWorked,
          reg:         num(form.reg),
          ot1:         num(form.ot1),
          ot2:         num(form.ot2),
          hol:         num(form.hol),
          sic:         num(form.sic),
          pto:         num(form.pto),
          meeting:     num(form.meeting),
          training:    num(form.training),
          lunch:       num(form.lunch),
          mileage:     num(form.mileage),
          otherPay:    num(form.otherPay),
          notes:       form.notes,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to submit');
      setSubmitMsg(`Time entry submitted for ${mgr.firstName} ${mgr.lastName}.`);
      setForm(prev => ({ ...EMPTY_FORM, managerId: prev.managerId }));
      fetchEntries();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      const res = await fetch(`/api/payroll/entry/${entryId}`, {
        method:  'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      fetchEntries();
    } catch (err) { alert(err.message); }
  };

  const downloadTemplate = () => {
    const headers = ['First Name','Last Name','Date','Regular Hours','OT1 Hours','OT2 Hours','Holiday Hours','Sick Hours','PTO Hours','Meeting Hours','Training Hours','Lunch (min)','Mileage','Other Pay','Notes'];
    const exampleMgr = managers[0];
    const example = exampleMgr ? [exampleMgr.firstName, exampleMgr.lastName, new Date().toISOString().split('T')[0], 8, 0, 0, 0, 0, 0, 0, 0, 30, 0, 0, ''] : [];
    const csv = [headers, example].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'manager-time-entry-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const isAdmin = isSuperAdmin() || user?.role === 'admin';
  if (!isAdmin) {
    return (
      <div className="admin-dashboard">
        <div className="dashboard-content" style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const fieldStyle = { width: '100%', padding: '0.5rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontWeight: '600', fontSize: '0.75rem', color: '#374151', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em' };

  const filteredEntries = filterMgr
    ? entries.filter(e => e.employeeId === filterMgr)
    : entries;

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <section className="welcome-section">
          <h1>Manager Time Entry</h1>
          <p className="subtitle">Submit and manage time entries for managers</p>
        </section>

        {/* ── Entry Form ── */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>New Entry</h2>
            <button onClick={downloadTemplate} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}>
              <IcoDownload size={14}/> CSV Template
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Manager + Date */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Manager *</label>
                <select value={form.managerId} onChange={e => handleChange('managerId', e.target.value)} style={fieldStyle} required>
                  <option value="">Select manager…</option>
                  {managers.map(m => (
                    <option key={m._id} value={m._id}>{m.firstName} {m.lastName} ({m.role})</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Date *</label>
                <input type="date" value={form.date} onChange={e => handleChange('date', e.target.value)} style={fieldStyle} required />
              </div>
            </div>

            {/* Hours grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
              {[
                { label: 'Regular (hrs)',   field: 'reg'      },
                { label: 'Overtime 1.5x',   field: 'ot1'      },
                { label: 'Double Time 2x',  field: 'ot2'      },
                { label: 'Holiday (hrs)',    field: 'hol'      },
                { label: 'Sick (hrs)',       field: 'sic'      },
                { label: 'PTO (hrs)',        field: 'pto'      },
                { label: 'Meeting (hrs)',    field: 'meeting'  },
                { label: 'Training (hrs)',   field: 'training' },
                { label: 'Lunch (min)',      field: 'lunch',   step: '1' },
                { label: 'Mileage (miles)',  field: 'mileage', step: '0.1' },
                { label: 'Other Pay ($)',    field: 'otherPay' },
              ].map(({ label, field, step }) => (
                <div key={field}>
                  <label style={labelStyle}>{label}</label>
                  <input
                    type="number" min="0" step={step || '0.01'}
                    value={form[field]} placeholder="0"
                    onChange={e => handleChange(field, e.target.value)}
                    style={fieldStyle}
                  />
                </div>
              ))}
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Notes</label>
              <input type="text" value={form.notes} onChange={e => handleChange('notes', e.target.value)} placeholder="Optional notes…" style={fieldStyle} />
            </div>

            {submitMsg   && <p style={{ color: '#10b981', fontWeight: '600', marginBottom: '0.75rem' }}>{submitMsg}</p>}
            {submitError && <p style={{ color: '#ef4444', fontWeight: '600', marginBottom: '0.75rem' }}>{submitError}</p>}

            <button type="submit" disabled={submitting} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.65rem 1.5rem', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.95rem', opacity: submitting ? 0.7 : 1 }}>
              <IcoCheck size={16}/> {submitting ? 'Submitting…' : 'Submit Entry'}
            </button>
          </form>
        </div>

        {/* ── Submitted Entries ── */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>Submitted Entries</h2>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={{ ...labelStyle, marginBottom: '0.2rem' }}>From</label>
                <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} style={{ ...fieldStyle, width: 'auto' }} />
              </div>
              <div>
                <label style={{ ...labelStyle, marginBottom: '0.2rem' }}>To</label>
                <input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} style={{ ...fieldStyle, width: 'auto' }} />
              </div>
              <select value={filterMgr} onChange={e => setFilterMgr(e.target.value)} style={{ ...fieldStyle, width: 'auto' }}>
                <option value="">All managers</option>
                {managers.map(m => <option key={m._id} value={m._id}>{m.firstName} {m.lastName}</option>)}
              </select>
            </div>
          </div>

          {loadingEntries ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>Loading entries…</p>
          ) : filteredEntries.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No manager time entries found for this period.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                    {['Manager','Date','Reg','OT1','OT2','HOL','SIC','PTO','Mtg','Trn','Lunch','Miles','Other','Notes',''].map(h => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: h === 'Manager' || h === 'Notes' || h === '' ? 'left' : 'right', fontWeight: '700', color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry, idx) => (
                    <tr key={entry._id} style={{ borderBottom: '1px solid #f3f4f6', background: idx % 2 === 0 ? 'white' : '#fdf4ff' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{entry.employeeName}</td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>{new Date(entry.date).toLocaleDateString(undefined, { timeZone: 'UTC' })}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{(entry.reg || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: (entry.ot1 || 0) > 0 ? '#f59e0b' : '#9ca3af' }}>{(entry.ot1 || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: (entry.ot2 || 0) > 0 ? '#ef4444' : '#9ca3af' }}>{(entry.ot2 || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: (entry.hol || 0) > 0 ? '#ec4899' : '#9ca3af' }}>{(entry.hol || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: (entry.sic || 0) > 0 ? '#8b5cf6' : '#9ca3af' }}>{(entry.sic || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: (entry.pto || 0) > 0 ? '#10b981' : '#9ca3af' }}>{(entry.pto || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: (entry.meeting || 0) > 0 ? '#0891b2' : '#9ca3af' }}>{(entry.meeting || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: (entry.training || 0) > 0 ? '#7c3aed' : '#9ca3af' }}>{(entry.training || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{entry.lunch || 0}m</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: (entry.mileage || 0) > 0 ? '#22c55e' : '#9ca3af' }}>{(entry.mileage || 0).toFixed(1)}mi</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: (entry.otherPay || 0) > 0 ? '#374151' : '#9ca3af' }}>${(entry.otherPay || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.notes || '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        <button onClick={() => setConfirmDelete({ id: entry._id })} style={{ padding: '0.25rem 0.5rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center' }}>
                          <IcoTrash size={13}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDeleteModal
          message="Delete this manager time entry? This cannot be undone."
          onConfirm={() => { handleDelete(confirmDelete.id); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
